<template lang="pug">
  v-container.justify-center.pa-0
    v-card(flat width="800px" class="rounded-card mx-auto" d-flex justify-center)
      v-snackbar( v-model="snackbar" color="red darken-2" ) {{ errorMessage }}
        template( v-slot:action="{attrs}")
          v-btn(text v-bind="attrs"
            @click="snackbar = false") {{ $t('buttons.closeButtonLabel') }}
      v-form( @submit.prevent="onSaveButtonClick" lazy-validation
        v-model="valid" ref="form"
        :disabled="saveInProgress")
        v-card.d-flex.flex-column.align-self-start.fill-height( v-if="!fetchInProgress"
          max-width="600px" flat)
          v-card-text.pb-0
            v-row
              v-col( col="7")
                v-list-item.text-h6.pl-0 {{ $t('billingTab.nameSignLabel') }}
              v-col.pb-0( cols=12)
                v-text-field(outlined
                v-model="name"
                :rules="nameRules"
                  :label=" $t('billingTab.nameTextFieldLabel') ")
              v-col.pt-0.pb-0( cols=12)
                v-text-field(outlined
                v-model="street"
                  :rules="streetRules"
                  :label=" $t('billingTab.addressTextFieldLabel') ")
              v-col.pt-0.pb-0( cols=8)
                v-text-field(outlined
                v-model="city"
                  :rules="cityRules"
                  :label=" $t('billingTab.townTextFieldLabel') ")
              v-col.pt-0.pb-0( cols=4)
                v-text-field(outlined
                v-model="zip"
                  :rules="zipRules"
                  :label=" $t('billingTab.zipTextFieldLabel') ")
            v-row
              v-col(cols="7")
                v-list-item.text-h6.pl-0 {{ $t('billingTab.bankAccountSignLabel') }}
              v-col(cols="12")
                v-text-field(outlined
                v-model="formattedIban"
                  :rules="ibanRules"
                @change="onFormattedIbanChange"
                  :label=" $t('billingTab.ibanTextFieldLabel') ")
          v-card-actions.justify-end.pt-0
            v-btn.mr-5( :disabled="!valid" type="submit"
              :loading="saveInProgress"
              color="primary") {{ $t('buttons.saveButtonLabel') }}
        v-skeleton-loader( v-if="fetchInProgress"
          type="list-item@12"
          max-width="600px")
</template>

<script lang="ts">
import Vue from "vue"
import {Component} from 'vue-property-decorator'
import {Action} from "vuex-class"
import {
  CREATE_BANK_ACCOUNT,
  CREATE_POSTAL_ADDRESS, CREATE_TENANT_NAME,
  FETCH_BANK_ACCOUNT,
  FETCH_POSTAL_ADDRESS, FETCH_TENANT_NAME, UPDATE_TENANT_NAME, UPDATE_BANK_ACCOUNT, UPDATE_POSTAL_ADDRESS
} from "@/store/action-types";
import i18n from "@/i18n";
import PostalAddress from "@/model/tennant/PostalAddress";
import BankAccount from "@/model/tennant/BankAccount";
import TenantName from "@/model/tennant/TenantName";
import {
  BankAccountPayload, PayloadTenantName,
  PayloadUpdateAddress,
  PayloadUpdateBank,
  PostalAddressPayload,
  TenantNamePayload, UpdatedBankAccountPayload, UpdatedPostalAddressPayload, UpdatedTenantNamePayload
} from "@/types/payload";

@Component({
  components: {}
})

export default class BillingTab extends Vue {
  $refs!: {
    form: Vue & {
      validate(): boolean
      resetValidation(): boolean
    }
  }

  @Action(FETCH_POSTAL_ADDRESS) fetchPostalAddress!: { (): Promise<PostalAddress[]> }
  @Action(FETCH_BANK_ACCOUNT) fetchBankAccount!: { (): Promise<BankAccount[]> }
  @Action(FETCH_TENANT_NAME) fetchTenantName!: { (): Promise<TenantName[]> }
  @Action(CREATE_POSTAL_ADDRESS) createPostalAddress!: { (newPostalData: PostalAddressPayload): Promise<PostalAddress> }
  @Action(CREATE_BANK_ACCOUNT) createBankAccount!: { (newBankData: BankAccountPayload): Promise<BankAccount> }
  @Action(CREATE_TENANT_NAME) createTenantName!: { (newTenantData: TenantNamePayload): Promise<TenantName> }
  @Action(UPDATE_POSTAL_ADDRESS) updatePostalAddress!: { (updatedPostalData: PayloadUpdateAddress): Promise<PostalAddress> }
  @Action(UPDATE_BANK_ACCOUNT) updateBankAccount!: { (updatedBankData: PayloadUpdateBank): Promise<BankAccount> }
  @Action(UPDATE_TENANT_NAME) updateTenantName!: { (updatedTenantData: PayloadTenantName): Promise<TenantName> }

  private fetchInProgress: boolean = false
  private saveInProgress: boolean = false

  private errorMessage: string = ''
  snackbar: boolean = false

  private postalAddress: PostalAddress | null = null
  private bankAccount: BankAccount | null = null
  private tenantName: TenantName | null = null
  private allPostalAddressData: PostalAddress[] | null = null
  private allBankAccountData: BankAccount[] | null = null
  private allTenantNameData: TenantName[] | null = null
  private filteredPostalAddress: PostalAddress | undefined = undefined
  private filteredBankAccount: BankAccount | undefined = undefined
  private filteredTenantName: TenantName | undefined = undefined

  private valid: boolean = false
  private isPostalAddressNew: boolean = true
  private isBankAccountNew: boolean = true
  private isTenantNameNew: boolean = true

  private data: [PostalAddress[], BankAccount[], TenantName[]] = [[], [], []]

  private key: string = 'tenant.billing.supplier_name'
  private kind: string = 'billing'

  private name: string = ''
  private nameRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('billingTab.nameValidationText').toString()
  ]

  private street: string = ''
  private streetRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('billingTab.streetValidationText').toString()
  ]

  private city: string = ''
  private cityRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('billingTab.cityValidationText').toString()
  ]

  private zip: string = ''
  private zipRules: Array<(v: number) => string | boolean> = [
    (v: number) => !!v || i18n.t('billingTab.zipValidationText').toString()
  ]

  private iban: string = ''
  private formattedIban: string | null = null
  private ibanRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v,
    (v: string) => {
      const pattern = /^[A-Z]{2}[0-9\s]+$/gm
      return pattern.test(v) || i18n.t('billingTab.ibanValidationText').toString()
    }
  ]

  onSaveButtonClick() {
    if (this.$refs.form.validate()) {
      this.sendData()
    }
  }

  onFormattedIbanChange() {
    this.formattedIban = this.formattedIban!.replace(/\S{4}(?=.)/g, '$& ')
    this.iban = this.formattedIban.replace(/\s/g, '')
  }

  async sendData(): Promise<void> {

    try {
      this.saveInProgress = true
      await Promise.all([this.sendPostalData(), this.sendBankData(), this.sendTenantData()])
      await this.fetchData()
    } catch (e) {
      this.errorMessage = this.$t('errors.saveErrorMessage').toString()
      this.snackbar = true
    } finally {
      this.saveInProgress = false
    }
  }

  async sendPostalData(): Promise<void> {
    if (this.isPostalAddressNew) {
      let newPostalData: PostalAddressPayload = {
        kind: this.kind,
        street: this.street,
        city: this.city,
        zip: this.zip
      }

      await this.createPostalAddress(newPostalData)
    } else {
      let changedPostalData: UpdatedPostalAddressPayload = {
        street: this.street,
        city: this.city,
        zip: this.zip
      }

      const postalPayload: PayloadUpdateAddress = {id: this.postalAddress!.id, changedData: changedPostalData}

      await this.updatePostalAddress(postalPayload)
    }
  }

  async sendBankData(): Promise<void> {
    if (this.isBankAccountNew) {
      let newBankData: BankAccountPayload = {
        kind: this.kind,
        iban: this.iban
      }

      await this.createBankAccount(newBankData)
    } else {
      let changedBankData: UpdatedBankAccountPayload = {
        iban: this.iban
      }

      const bankPayload: PayloadUpdateBank = {id: this.bankAccount!.id, changedData: changedBankData}

      await this.updateBankAccount(bankPayload)
    }
  }

  async sendTenantData(): Promise<void> {
    if (this.isTenantNameNew) {
      let newTenantData: TenantNamePayload = {
        key: this.key,
        value: this.name
      }

      await this.createTenantName(newTenantData)
    } else {
      let changedTenantData: UpdatedTenantNamePayload = {
        value: this.name
      }

      const tenantPayload: PayloadTenantName = {id: this.tenantName!.id, changedData: changedTenantData}

      await this.updateTenantName(tenantPayload)
    }
  }

  setData(): void {
    if (this.filteredPostalAddress) {
      this.street = this.postalAddress!.street
      this.city = this.postalAddress!.city
      this.zip = this.postalAddress!.zip
    }
    if (this.filteredBankAccount) {
      this.iban = this.bankAccount!.iban
      this.formattedIban = this.iban.toString().replace(/\w{4}(?=.)/g, '$& ')
    }
    if (this.filteredTenantName) {
      this.name = this.tenantName!.value
    }
  }

  async fetchData(): Promise<void> {
    this.fetchInProgress = true

    try {
      this.data = await Promise.all([this.fetchPostalAddress(), this.fetchBankAccount(), this.fetchTenantName()])
      this.allPostalAddressData = this.data[0]
      this.allBankAccountData = this.data[1]
      this.allTenantNameData = this.data[2]
      this.filterDataKind()
      this.setData()
    } catch (e) {
      this.errorMessage = this.$t('errors.fetchDataError').toString()
      this.snackbar = true
    } finally {
      this.fetchInProgress = false
    }
  }

  filterDataKind(): void {
    this.filteredPostalAddress = this.allPostalAddressData?.find(obj => {
      return obj.kind.name == 'billing'
    })
    this.filteredBankAccount = this.allBankAccountData?.find(obj => {
      return obj.kind.name == 'billing'
    })
    this.filteredTenantName = this.allTenantNameData?.find(obj => {
      return obj.key == 'tenant.billing.supplier_name'
    })
    if (this.filteredPostalAddress) {
      this.postalAddress = this.filteredPostalAddress
      this.isPostalAddressNew = false
    }
    if (this.filteredBankAccount) {
      this.bankAccount = this.filteredBankAccount
      this.isBankAccountNew = false
    }
    if (this.filteredTenantName) {
      this.tenantName = this.filteredTenantName
      this.isTenantNameNew = false
    }
  }

  mounted(): void {
    this.fetchData()
  }

}
</script>

<style lang="stylus" scoped>

</style>
